import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Loader2, Bot, ChevronDown, ChevronUp, CheckCircle2, MessageCircle } from 'lucide-react'
import {
  botSettingsApi,
  DEFAULT_FAQ_BRIDGE_CONFIG,
  type FaqBridgeConfig,
  type FaqBridgePreset,
} from '@/services/botSettingsApi'

const schema = z.object({
  productBridgeText: z.string().min(1, 'El texto con producto es requerido'),
  multipleProductsBridgeText: z.string().min(1, 'El texto con múltiples productos es requerido'),
  genericBridgeText: z.string().min(1, 'El texto genérico es requerido'),
  productStatementText: z.string().min(1, 'La frase de seguimiento con producto es requerida'),
  multipleProductsStatementText: z.string().min(1, 'La frase de seguimiento múltiple es requerida'),
  genericStatementText: z.string().min(1, 'La frase de seguimiento genérica es requerida'),
})

type FormValues = z.infer<typeof schema>

function toFormValues(value?: FaqBridgeConfig | null): FormValues {
  const config = value ?? DEFAULT_FAQ_BRIDGE_CONFIG
  return {
    productBridgeText: config.productBridgeText,
    multipleProductsBridgeText: config.multipleProductsBridgeText,
    genericBridgeText: config.genericBridgeText,
    productStatementText: config.productStatementText,
    multipleProductsStatementText: config.multipleProductsStatementText,
    genericStatementText: config.genericStatementText,
  }
}

function matchesPreset(values: FormValues, preset: FaqBridgePreset): boolean {
  return (
    values.productBridgeText === preset.config.productBridgeText &&
    values.multipleProductsBridgeText === preset.config.multipleProductsBridgeText &&
    values.genericBridgeText === preset.config.genericBridgeText &&
    values.productStatementText === preset.config.productStatementText &&
    values.multipleProductsStatementText === preset.config.multipleProductsStatementText &&
    values.genericStatementText === preset.config.genericStatementText
  )
}

function formatWhatsAppText(raw: string, product = 'Laptop HP 15', productB = 'Dell Inspiron'): string {
  return raw
    .replace(/\{\{product\}\}/g, product)
    .replace(/\{\{productA\}\}/g, product)
    .replace(/\{\{productB\}\}/g, productB)
}

export default function FAQBridgeSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [presets, setPresets] = useState<FaqBridgePreset[]>([])
  const [selectedPresetId, setSelectedPresetId] = useState<string>('venta_suave')
  const [showAdvanced, setShowAdvanced] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema as any),
    defaultValues: toFormValues(),
  })

  useEffect(() => {
    let mounted = true

    const loadSettings = async () => {
      try {
        setLoading(true)
        const data = await botSettingsApi.getSettings()
        if (!mounted) return
        setPresets(data.faqBridgePresets ?? [])
        setSelectedPresetId(data.faqBridgePresetId ?? data.faqBridgePresets?.[0]?.id ?? 'venta_suave')
        reset(toFormValues(data.faqBridgeConfig))
      } catch (error) {
        console.error('Error loading FAQ bridge settings:', error)
        toast.error('No se pudo cargar la configuración del retorno comercial')
        setPresets([])
        setSelectedPresetId('venta_suave')
        reset(toFormValues())
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadSettings()

    return () => {
      mounted = false
    }
  }, [reset])

  const currentValues = watch()
  const activePreset = presets.find((preset) => matchesPreset(currentValues, preset))

  const applyPreset = (preset: FaqBridgePreset) => {
    setSelectedPresetId(preset.id)
    reset(toFormValues(preset.config))
    toast.success(`Estilo aplicado: ${preset.label}`)
  }

  const onSubmit = async (values: FormValues) => {
    try {
      setSaving(true)
      await botSettingsApi.updateSettings({
        faqBridgeConfig: values,
        faqBridgePresetId: selectedPresetId,
      })
      toast.success('Configuración de continuidad comercial guardada')
    } catch (error) {
      console.error('Error saving FAQ bridge settings:', error)
      toast.error('Error al guardar la configuración')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#075E54]" />
      </div>
    )
  }

  const sampleBridgeWithProduct = formatWhatsAppText(watch('productBridgeText') || '')

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-4 sm:p-6">
      {/* Header explicativo para dueños de tienda */}
      <div className="p-4 rounded-2xl border border-emerald-200/80 bg-gradient-to-r from-emerald-50/80 to-teal-50/40">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#075E54] flex items-center justify-center flex-shrink-0 shadow-sm">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm sm:text-base">
              Continuidad de Venta tras responder Dudas Frecuentes
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Cuando un cliente pregunta por envíos, pagos o garantías, el asistente responde su duda e inmediatamente
              retoma la venta de forma amigable para evitar conversaciones frías o abandonos.
            </p>
          </div>
        </div>
      </div>

      {/* Selector de Estilo de Atención (1 Clic) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-gray-900">¿Cómo debe continuar el Asistente tras responder una duda?</h4>
            <p className="text-xs text-gray-500">
              Selecciona el estilo que mejor represente a tu negocio. El sistema ajusta automáticamente el tono.
            </p>
          </div>
          {activePreset && (
            <span className="hidden sm:inline-flex rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold">
              Activo: {activePreset.label}
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {presets.map((preset) => {
            const isSelected = selectedPresetId === preset.id || activePreset?.id === preset.id

            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`relative text-left rounded-2xl border-2 p-4 transition-all duration-200 shadow-sm ${
                  isSelected
                    ? 'border-[#075E54] bg-emerald-50/40 shadow-md ring-1 ring-[#075E54]'
                    : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h5 className="text-sm font-bold text-gray-900">{preset.label}</h5>
                  {isSelected ? (
                    <CheckCircle2 className="w-5 h-5 text-[#075E54] flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border border-gray-300 flex-shrink-0" />
                  )}
                </div>
                <p className="mt-1.5 text-xs text-gray-600 leading-relaxed">{preset.description}</p>
                <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-1.5 text-[11px] font-medium text-emerald-800">
                  <span>{isSelected ? '✓ Estilo seleccionado' : 'Pulsa para aplicar'}</span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Simulación visual de WhatsApp en Tiempo Real */}
      <div className="rounded-2xl border border-gray-200 bg-slate-50/70 p-4 sm:p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            <MessageCircle className="w-4 h-4 text-[#075E54]" />
            <span>Vista Previa: Así le responderá a tus clientes</span>
          </div>
          <span className="text-[11px] text-gray-400">Ejemplo de consulta por envío</span>
        </div>

        <div className="space-y-2.5 max-w-lg mx-auto bg-[#E5DDD5]/40 p-4 rounded-2xl border border-gray-200">
          {/* Mensaje del cliente */}
          <div className="flex justify-end">
            <div className="bg-[#E7FFDB] text-gray-900 text-xs sm:text-sm rounded-2xl rounded-tr-none px-3.5 py-2 shadow-sm max-w-[85%]">
              <p>Hola, ¿hacen envíos a domicilio y cuánto tarda?</p>
              <span className="text-[10px] text-gray-400 block text-right mt-1">10:42 AM</span>
            </div>
          </div>

          {/* Respuesta del bot */}
          <div className="flex justify-start">
            <div className="bg-white text-gray-900 text-xs sm:text-sm rounded-2xl rounded-tl-none px-3.5 py-2.5 shadow-sm max-w-[90%] space-y-2 border border-gray-100">
              <p className="text-gray-800">
                ¡Sí! Hacemos despachos directos y entregas seguras en 24 a 48 horas hábiles. 🚚
              </p>
              <div className="border-t border-dashed border-gray-200 pt-2 font-medium text-emerald-950">
                {sampleBridgeWithProduct || '¿Te gustaría continuar con tu pedido?'}
              </div>
              <span className="text-[10px] text-gray-400 block text-right">10:42 AM ✓✓</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modo avanzado colapsable (Opcional) */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 text-left text-xs font-semibold text-gray-600 hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2">
            <span>⚙️ Personalizar frases exactas</span>
            <span className="text-gray-400 font-normal">(Opcional para edición detallada)</span>
          </div>
          {showAdvanced ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {showAdvanced && (
          <div className="p-4 sm:p-5 border-t border-gray-100 space-y-4 bg-gray-50/50">
            <p className="text-xs text-gray-500">
              Si deseas redactar frases personalizadas, asegúrate de mantener los marcadores de producto intactos:{' '}
              <code>{'{{product}}'}</code>, <code>{'{{productA}}'}</code> y <code>{'{{productB}}'}</code>.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Continuación con 1 producto
                </label>
                <textarea
                  {...register('productBridgeText')}
                  rows={3}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                  placeholder={DEFAULT_FAQ_BRIDGE_CONFIG.productBridgeText}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Continuación comparando 2 productos
                </label>
                <textarea
                  {...register('multipleProductsBridgeText')}
                  rows={3}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                  placeholder={DEFAULT_FAQ_BRIDGE_CONFIG.multipleProductsBridgeText}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Continuación genérica (Sin producto elegido)
                </label>
                <textarea
                  {...register('genericBridgeText')}
                  rows={3}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                  placeholder={DEFAULT_FAQ_BRIDGE_CONFIG.genericBridgeText}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Llamada a la acción con producto
                </label>
                <textarea
                  {...register('productStatementText')}
                  rows={3}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                  placeholder={DEFAULT_FAQ_BRIDGE_CONFIG.productStatementText}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Botón de guardado */}
      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          disabled={isSubmitting || saving}
          className="bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl px-6 py-2.5 font-medium shadow-sm"
        >
          {isSubmitting || saving ? 'Guardando...' : 'Guardar estilo de atención'}
        </Button>
      </div>
    </form>
  )
}
